import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Protected Route Component
 * Wraps routes that require authentication
 * Redirects to landing page if user is not authenticated
 * Monitors auth state changes for cross-tab logout
 * Handles browser back/forward cache restoration
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, authState } = useAuth();
  const navigate = useNavigate();

  // Monitor auth state changes (for cross-tab logout)
  useEffect(() => {
    if (!isLoading && !isAuthenticated()) {
      console.log('🚪 [ProtectedRoute] Authentication lost - Redirecting to landing page');
      navigate('/', { replace: true });
    }
  }, [authState.isAuthenticated, isLoading, isAuthenticated, navigate]);

  // Handle browser back/forward cache (bfcache) restoration
  useEffect(() => {
    const handlePageShow = (event) => {
      // Check if page was restored from bfcache
      if (event.persisted) {
        console.log('🔄 [ProtectedRoute] Page restored from cache - re-validating authentication');
        
        // Re-check authentication from localStorage
        const savedAuthState = localStorage.getItem('authState');
        if (!savedAuthState) {
          console.log('⚠️ [ProtectedRoute] No auth state in cache - redirecting to login');
          window.location.href = '/';
        } else {
          try {
            const parsedState = JSON.parse(savedAuthState);
            if (!parsedState.isAuthenticated) {
              console.log('⚠️ [ProtectedRoute] Not authenticated - redirecting to login');
              window.location.href = '/';
            }
          } catch (error) {
            console.error('❌ [ProtectedRoute] Error parsing cached auth state:', error);
            window.location.href = '/';
          }
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Wait for auth state to load from localStorage
  if (isLoading) {
    console.log('⏳ [ProtectedRoute] Loading authentication state...');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated()) {
    console.log('⚠️ [ProtectedRoute] Unauthenticated access attempt - Redirecting to landing page');
    return <Navigate to="/" replace />;
  }

  console.log('✅ [ProtectedRoute] Authenticated access granted');
  return children;
};

export default ProtectedRoute;
